import jsPDF from 'jspdf';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { projectService } from '../services/projectService';
import {
  hostPresentsDocuments,
  notifyDocumentSaved,
  openDocumentUrl,
} from './documentsHostBridge';

export const exportParcelBoundariesToPDF = async (
  propertyInfo,
  mapImageData,
  projectName = 'Property',
  projectId = null
) => {
  try {
    // Create PDF in landscape mode for better map viewing
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'letter',
    });

    // Get page dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;

    // Add header
    pdf.setFontSize(18);
    pdf.setFont(undefined, 'bold');
    pdf.text('Parcel Boundaries', margin, 20);

    // Add property address
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'normal');
    if (propertyInfo?.address) {
      pdf.text(propertyInfo.address, margin, 28);
    }

    // Add APN
    if (propertyInfo?.apn) {
      pdf.setFontSize(10);
      pdf.text(`APN: ${propertyInfo.apn}`, margin, 35);
    }

    // Add timestamp
    pdf.setFontSize(9);
    pdf.setTextColor(100);
    pdf.text(
      `Generated: ${new Date().toLocaleString()}`,
      pageWidth - margin - 60,
      20
    );
    pdf.setTextColor(0);

    // Add horizontal line
    pdf.setLineWidth(0.3);
    pdf.line(margin, 38, pageWidth - margin, 38);

    // Add map
    if (mapImageData) {
      try {
        // Calculate map dimensions to fill most of the page
        const maxMapWidth = pageWidth - 2 * margin;
        const maxMapHeight = pageHeight - 50; // Leave space for header

        // Add map image centered
        pdf.addImage(
          mapImageData,
          'PNG',
          margin,
          42,
          maxMapWidth,
          maxMapHeight,
          undefined,
          'FAST'
        );
      } catch (error) {
        console.error('Failed to add map image to PDF:', error);
        // If image fails, add a note
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'italic');
        pdf.setTextColor(100);
        pdf.text(
          'Parcel map could not be loaded. Please try again.',
          pageWidth / 2,
          pageHeight / 2,
          { align: 'center' }
        );
        pdf.setTextColor(0);
      }
    } else {
      // No map data
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(100);
      pdf.text(
        'No parcel map available. Please search for property information first.',
        pageWidth / 2,
        pageHeight / 2,
        { align: 'center' }
      );
      pdf.setTextColor(0);
    }

    // Generate filename
    const timestamp = new Date().toISOString().slice(0, 10);
    const safeProjectName = projectName.replace(/[^a-z0-9]/gi, '_');
    const filename = `parcel_boundaries_${safeProjectName}_${timestamp}.pdf`;

    // Save to Firebase if projectId is provided
    let downloadUrl = null;
    if (projectId) {
      try {
        // Convert PDF to blob
        const pdfBlob = pdf.output('blob');

        // Upload to Firebase Storage
        const storagePath = `projects/${projectId}/parcel-boundaries/${filename}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, pdfBlob);
        downloadUrl = await getDownloadURL(storageRef);

        // Get existing project data
        const project = await projectService.getProject(projectId);

        // Prepare document metadata
        const docMetadata = {
          filename,
          url: downloadUrl,
          createdAt: new Date().toISOString(),
          type: 'parcel-boundaries',
          storagePath,
          address: propertyInfo?.address || '',
          apn: propertyInfo?.apn || '',
        };

        // Update project with parcel boundaries document
        const parcelDocs = project.parcelBoundaryDocs || [];
        parcelDocs.push(docMetadata);

        await projectService.updateProject(projectId, {
          parcelBoundaryDocs: parcelDocs,
        });
        notifyDocumentSaved({
          docType: 'parcelMap',
          firestoreKey: 'parcelBoundaryDocs',
          url: downloadUrl,
          name: filename,
        });

        console.log('Parcel boundaries PDF saved to project documents');
      } catch (error) {
        console.error('Error saving parcel boundaries to project:', error);
        // Continue with local download even if cloud save fails
      }
    }

    // Download the PDF. A WKWebView host can't download, so it opens the
    // uploaded copy natively instead — when the upload got that far.
    if (hostPresentsDocuments() && downloadUrl) {
      openDocumentUrl(downloadUrl, { name: filename });
    } else {
      pdf.save(filename);
    }

    return {
      success: true,
      filename,
      message: 'Parcel boundaries PDF exported successfully',
    };
  } catch (error) {
    console.error('Error generating parcel boundaries PDF:', error);
    return {
      success: false,
      error: error.message,
      message: 'Failed to export parcel boundaries PDF',
    };
  }
};
